import type { GetServerSideProps } from 'next';
import { renderToString } from 'react-dom/server';
import {
  Configure,
  getServerState,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch';

import { parseUiStateFilters } from '@/utils/algolia';
import { getLocaleFromParams } from '@/utils/locale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import { DummyFilterActivator } from '@/components/Dummy/DummyFilterActivator';
import { DummyHits } from '@/components/Dummy/DummyHits';
import { DummyRangeActivator } from '@/components/Dummy/DummyRangeActivator';
import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { fetchListingPageData } from '@/integrations/contentful';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import type { LocaleMessages } from '@/lib/intl';
import { PLP_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import type { AllTheStrapsViewProps } from '@/views/AllTheStrapsView';
import { AllTheStrapsView } from '@/views/AllTheStrapsView';

export function DummyAllStrapsPage({
  draftMode,
  filterAttributes,
  filters,
  indexName,
  initialUiState,
  page,
  userToken,
}: {
  draftMode?: boolean;
  filterAttributes: string[];
  filters: string;
  indexName: string;
  initialUiState?: SearchUiState;
  page?: number;
  userToken?: string;
}) {
  const ssrClient = createSearchClient({
    isSsr: true,
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_STRAPS,
  });

  return (
    <InstantSearchSSRProvider>
      <InstantSearch
        indexName={indexName}
        initialUiState={initialUiState}
        insights={false}
        searchClient={ssrClient}
      >
        <Configure
          analyticsTags={[
            'platform:web',
            'product:straps',
            'page:listing',
            'context:all-straps',
          ]}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          facets={['*']}
          filters={filters}
          page={page}
          userToken={userToken}
        />
        <DummyHits />
        <DummyRangeActivator />
        {filterAttributes.map((attribute) => (
          <DummyFilterActivator key={attribute} attribute={attribute} />
        ))}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}

export const getServerSideProps: GetServerSideProps<
  AllTheStrapsViewProps & {
    localeMessages: LocaleMessages;
  }
> = commonServerSideProps(async (context) => {
  setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);
  const { query, resolvedUrl } = context;
  const draftMode = resolveContentDraftMode(context);
  const routerLocale = getLocaleFromParams(context?.params);

  const region = getRegionFromLocale(routerLocale);
  const contentfulLocale = region.locale.contentful ?? region.locale.locale;

  const indexName = getIndexName(
    'PRODUCT',
    routerLocale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );
  const initialUiState = buildUiStateFromURL(
    resolvedUrl,
    indexName,
    undefined,
    undefined,
    region.locale,
  );

  const { countryCode } = getContentfulRegionFromLocale(routerLocale);
  const {
    cmsData: allTheStrapsCmsData,
    cmsDefaultLayoutData,
    seoConfigBuilder,
  } = await fetchListingPageData(
    'all the straps',
    contentfulLocale,
    null,
    null,
    null,
    countryCode,
    region.locale.code,
    draftMode,
  );

  const strapFilters = `(strap_length.EN_GB:'Standard' OR strap_length.EN_US:'Standard' OR strap_length.EN:'Standard')`;
  const algoliaFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.PLP,
    'Strap',
    draftMode,
    strapFilters,
  );

  const pageIndex = query?.page && parseInt(query.page as string);

  // Perform initial search to get facets (without URL filters for translation)
  const algoliaSsrSearchClient = createSearchClient({
    isSsr: true,
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_STRAPS,
  });
  const index = algoliaSsrSearchClient.initIndex(indexName);
  const searchResponse = await index.search('', {
    filters: algoliaFilters,
    facets: ['*'],
    maxValuesPerFacet: 1000,
  });

  // Translate URL filter values from English to the target locale using
  // searchResponse facets, so that the server-side Algolia search uses the
  // correct locale-specific values (e.g. color.ES:"Azul | BLU" instead of
  // color.ES:"Blue | BLU").
  const translatedUiState = buildUiStateFromURL(
    resolvedUrl,
    indexName,
    undefined,
    undefined,
    region.locale,
    {
      initialResults: {
        [indexName]: {
          results: [
            {
              facets: searchResponse.facets,
              renderingContent: searchResponse.renderingContent,
            },
          ],
        },
      },
    } as any,
    'EN_US',
  );

  const filterAttributes = Object.entries(
    translatedUiState[indexName]?.refinementList || {},
  )
    .filter(([, value]) => value && value.length > 0)
    .map(([key]) => key);

  // Convert translated URL query parameters to Algolia filters
  const urlFilters = parseUiStateFilters(translatedUiState[indexName]);

  // Combine collection filters with URL filters
  const combinedFilters = urlFilters
    ? algoliaFilters
      ? `${algoliaFilters} AND ${urlFilters}`
      : urlFilters
    : algoliaFilters;

  // Generate Algolia user token server-side for consistent SSR/client rendering
  const algoliaUserToken = getUserTokenForAlgoliaSSR(context.req) ?? null;

  const serverState = await getServerState(
    <DummyAllStrapsPage
      draftMode={draftMode}
      filterAttributes={filterAttributes}
      filters={combinedFilters}
      indexName={indexName}
      initialUiState={translatedUiState}
      userToken={algoliaUserToken ?? undefined}
      {...(typeof pageIndex === 'number' ? { page: pageIndex - 1 } : {})}
    />,
    {
      renderToString,
    },
  );

  // Inject facets from the unfiltered search into serverState for translation
  // This ensures facets are available even when URL filters don't match any products
  if (serverState.initialResults?.[indexName]?.results?.[0]) {
    serverState.initialResults[indexName].results[0].facets =
      searchResponse.facets;
  }

  const nbPages = serverState.initialResults[indexName].results[0].nbPages;

  if (typeof pageIndex === 'number' && (pageIndex > nbPages || pageIndex < 1)) {
    capture404Event('All Straps PLP', {
      requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
      reason: PLP_ERRORS.PLP_PAGINATION_OUT_OF_RANGE,
    });
    return {
      notFound: true,
    };
  }

  return {
    props: {
      algoliaFilters,
      algoliaUserToken,
      draftMode: draftMode ?? false,
      instantSearchProps: {
        initialUiState,
        indexName,
        serverState,
      },
      allTheStrapsCmsData,
      cmsDefaultLayoutData,
      seoConfig: seoConfigBuilder?.build(),
      serverState,
    },
  };
});

export default AllTheStrapsView;
